import { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { toast } from 'sonner';
import { 
  FileText, Download, Loader2, X, CheckCircle, AlertTriangle,
  FileOutput, ChevronDown, ChevronUp
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Group documents by category
const DOCUMENT_CATEGORIES = {
  'Comprehensive': ['change_pack'],
  'Governance': ['change_strategy', 'governance_framework', 'sponsor_briefing'],
  'Stakeholder': ['stakeholder_map', 'impact_assessment'],
  'Communications': ['comms_plan', 'comms_toolkit'],
  'Training': ['training_plan'],
  'Go-Live': ['readiness_checklist', 'cutover_plan'],
  'Closure & Reporting': ['benefits_realisation', 'lessons_learned', 'closure_report', 'status_report']
};

export default function DocumentsPanel({ projectId, onClose }) {
  const [outputTypes, setOutputTypes] = useState({});
  const [generatedOutputs, setGeneratedOutputs] = useState([]);
  const [generating, setGenerating] = useState(null);
  const [expandedCategory, setExpandedCategory] = useState('Comprehensive');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchOutputTypes();
    fetchGeneratedOutputs();
  }, [projectId]);

  const fetchOutputTypes = async () => {
    try {
      const response = await axios.get(`${API}/outputs/types`);
      setOutputTypes(response.data);
    } catch (error) {
      console.error('Failed to fetch output types:', error);
    }
  };

  const fetchGeneratedOutputs = async () => {
    try {
      const response = await axios.get(`${API}/outputs/${projectId}`);
      setGeneratedOutputs(response.data);
    } catch (error) {
      console.error('Failed to fetch outputs:', error);
    } finally {
      setLoading(false);
    }
  };

  const generateOutput = async (outputType) => {
    setGenerating(outputType);
    try {
      const response = await axios.post(`${API}/outputs/generate`, {
        project_id: projectId,
        output_type: outputType
      });
      toast.success(`${outputTypes[outputType]?.name || 'Document'} generated!`);
      setGeneratedOutputs(prev => [response.data, ...prev]);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to generate document');
    } finally {
      setGenerating(null);
    }
  };

  const downloadOutput = async (output, format = 'docx') => {
    try {
      const token = localStorage.getItem('token');
      
      const response = await axios.get(
        `${API}/outputs/${projectId}/${output.id}/download?format=${format}`,
        { 
          responseType: 'blob',
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );
      
      // Determine MIME type and extension based on format
      const mimeTypes = {
        'docx': 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        'pptx': 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
        'md': 'text/markdown'
      };
      const extensions = { 'docx': '.docx', 'pptx': '.pptx', 'md': '.md' };
      
      const url = window.URL.createObjectURL(new Blob([response.data], {
        type: mimeTypes[format] || mimeTypes['docx']
      }));
      const link = document.createElement('a');
      link.href = url;
      const filename = `${output.output_type}_${new Date().toISOString().split('T')[0]}${extensions[format] || '.docx'}`;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Document downloaded!');
    } catch (error) {
      console.error('Download error:', error);
      toast.error('Failed to download. Please try again.');
    }
  };

  const getExistingOutput = (outputType) => {
    return generatedOutputs.find(o => o.output_type === outputType);
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl w-full max-w-4xl max-h-[90vh] flex flex-col shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-amber-100 rounded-xl flex items-center justify-center">
              <FileOutput className="h-5 w-5 text-amber-600" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-slate-900">Generate Documents</h2>
              <p className="text-sm text-slate-500">Create change management outputs from your conversations</p>
            </div>
          </div>
          <Button variant="ghost" size="icon" onClick={onClose}>
            <X className="h-5 w-5" />
          </Button>
        </div>

        {/* Content */}
        <ScrollArea className="flex-1 p-6">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
            </div>
          ) : (
            <div className="space-y-4">
              {Object.entries(DOCUMENT_CATEGORIES).map(([category, types]) => (
                <div key={category} className="border rounded-xl overflow-hidden">
                  {/* Category Header */}
                  <button
                    onClick={() => setExpandedCategory(expandedCategory === category ? null : category)}
                    className="w-full flex items-center justify-between p-4 bg-slate-50 hover:bg-slate-100 transition-colors"
                  >
                    <span className="font-semibold text-slate-900">{category}</span>
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-slate-500">{types.length} documents</span>
                      {expandedCategory === category ? (
                        <ChevronUp className="h-5 w-5 text-slate-400" />
                      ) : (
                        <ChevronDown className="h-5 w-5 text-slate-400" />
                      )}
                    </div>
                  </button>

                  {/* Document List */}
                  {expandedCategory === category && (
                    <div className="divide-y">
                      {types.map(type => {
                        const config = outputTypes[type];
                        const existing = getExistingOutput(type);
                        if (!config) return null;

                        return (
                          <div key={type} className="p-4 flex items-center justify-between hover:bg-slate-50">
                            <div className="flex-1">
                              <div className="flex items-center gap-2">
                                <FileText className="h-4 w-4 text-slate-400" />
                                <span className="font-medium text-slate-900">{config.name}</span>
                                {existing && (
                                  <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full">
                                    Generated
                                  </span>
                                )}
                              </div>
                              <p className="text-sm text-slate-500 mt-1">{config.description}</p>
                            </div>
                            <div className="flex items-center gap-2">
                              {existing && (
                                <>
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => downloadOutput(existing, 'docx')}
                                    className="gap-2 border-blue-500 text-blue-600 hover:bg-blue-50"
                                  >
                                    <Download className="h-4 w-4" />
                                    Word
                                  </Button>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => downloadOutput(existing, 'pptx')}
                                    className="gap-1 text-slate-500 hover:text-slate-700"
                                    title="Download as PowerPoint"
                                  >
                                    <Download className="h-3 w-3" />
                                    PPT
                                  </Button>
                                </>
                              )}
                              )}
                              <Button
                                size="sm"
                                onClick={() => generateOutput(type)}
                                disabled={generating === type}
                                className="gap-2 bg-slate-900 hover:bg-slate-800"
                              >
                                {generating === type ? (
                                  <>
                                    <Loader2 className="h-4 w-4 animate-spin" />
                                    Generating...
                                  </>
                                ) : (
                                  <>
                                    <FileOutput className="h-4 w-4" />
                                    {existing ? 'Regenerate' : 'Generate'}
                                  </>
                                )}
                              </Button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </ScrollArea>

        {/* Footer */}
        <div className="p-4 border-t bg-slate-50 rounded-b-2xl">
          <p className="text-xs text-slate-500 text-center">
            Documents are generated from your project details, uploaded files, and chat conversations.
            The more context you provide, the more complete your outputs will be.
          </p>
        </div>
      </div>
    </div>
  );
}
